/*! Kind Paws Home - kp.js. Vanilla, no dependencies, loaded with `defer`.
   The markup contract (custom elements, data attributes, events) is in README.md under "kp.js contract".
   layout/theme.liquid adds `js` to <html> inline before first paint; this file adds `js-ready` once it has run. */
(() => {
'use strict';
const d = document, w = window, root = d.documentElement;
const reduced = () => w.matchMedia('(prefers-reduced-motion: reduce)').matches;
const $ = (s, r) => (r || d).querySelector(s);
const $$ = (s, r) => Array.from((r || d).querySelectorAll(s));
const emit = (el, n, detail) => el.dispatchEvent(new CustomEvent(n, { detail, bubbles: true }));
const define = (n, c) => customElements.get(n) || customElements.define(n, c);
const FOCUS = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

const money = (cents, fmt) => (fmt || root.dataset.moneyFormat || '${{amount}}')
  .replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => (Number(cents) / 100).toFixed(k.includes('no_decimals') ? 0 : 2).replace(/\B(?=(\d{3})+(?!\d))/g, ','));

let locks = 0;
const lock = () => {
  if (locks++) return;
  const sb = w.innerWidth - root.clientWidth;
  root.style.overflow = 'hidden';
  if (sb > 0) d.body.style.paddingRight = sb + 'px';
};
const unlock = () => {
  if (--locks > 0) return;
  locks = 0;
  root.style.overflow = '';
  d.body.style.paddingRight = '';
};

/* header + announcement */
define('kp-announcement', class extends HTMLElement {});
define('kp-header', class extends HTMLElement {
  connectedCallback() {
    const setH = () => root.style.setProperty('--kp-header-h', this.offsetHeight + 'px');
    if (w.ResizeObserver) new ResizeObserver(setH).observe(this); else setH();
    let raf = 0;
    const check = () => { raf = 0; this.classList.toggle('is-stuck', w.scrollY > 0 && this.getBoundingClientRect().top <= 0); };
    w.addEventListener('scroll', () => { raf = raf || requestAnimationFrame(check); }, { passive: true });
    check();
  }
});

/* drawer */
/* Only real disclosure triggers (aria-controls or data-kp-open) get aria-expanded; an Add to cart button that opened the drawer does not. */
const isToggle = (o) => !!(o && o.hasAttribute && (o.hasAttribute('aria-controls') || 'kpOpen' in o.dataset));
class KPDrawer extends HTMLElement {
  connectedCallback() {
    const dl = this.dialog = $('dialog', this);
    if (!dl || this.bound) return;
    this.bound = true;
    dl.addEventListener('click', (e) => {
      if (e.target.closest('[data-kp-close]')) { e.preventDefault(); this.close(); return; }
      const r = dl.getBoundingClientRect();
      if (e.target === dl && (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom)) this.close();
    });
    dl.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });
    dl.addEventListener('keydown', (e) => { if (e.key === 'Tab') this.trap(e); });
    dl.addEventListener('close', () => this.done());
  }
  get isOpen() { return !!(this.dialog && this.dialog.open); }
  open(opener) {
    const dl = this.dialog;
    if (!dl || !dl.showModal) return;
    if (dl.open) { clearTimeout(this.timer); this.removeAttribute('closing'); return; }
    this.opener = opener instanceof HTMLElement ? opener : d.activeElement;
    dl.showModal();
    lock();
    this.setAttribute('open', '');
    if (isToggle(this.opener)) this.opener.setAttribute('aria-expanded', 'true');
    const first = $('[autofocus]', dl) || $('[data-kp-close]', dl) || $(FOCUS, dl);
    if (first) first.focus();
    emit(this, 'kp:drawer-open');
  }
  close() {
    if (!this.isOpen || this.hasAttribute('closing')) return;
    const finish = () => { if (this.dialog.open) this.dialog.close(); this.done(); };
    if (reduced()) return finish();
    this.setAttribute('closing', '');
    this.timer = setTimeout(finish, parseInt(this.dataset.kpDuration, 10) || 260);
  }
  done() {
    if (!this.hasAttribute('open')) return;
    unlock();
    this.removeAttribute('open');
    this.removeAttribute('closing');
    const o = this.opener;
    this.opener = null;
    if (o) { if (isToggle(o)) o.setAttribute('aria-expanded', 'false'); if (o.isConnected && o.focus) o.focus(); }
    emit(this, 'kp:drawer-close');
  }
  trap(e) {
    const f = $$(FOCUS, this.dialog).filter((el) => el.getClientRects().length);
    if (!f.length) return;
    const a = d.activeElement, first = f[0], last = f[f.length - 1];
    if (e.shiftKey && (a === first || a === this.dialog)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && a === last) { e.preventDefault(); first.focus(); }
  }
}
define('kp-drawer', KPDrawer);
const drawer = (id) => { const el = d.getElementById(id || 'cart-drawer'); return el instanceof KPDrawer ? el : null; };

/* cart controller */
const HEADERS = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
const cartsBusy = (b) => $$('kp-cart').forEach((el) => el.setBusy && el.setBusy(b));
const cart = {
  count: 0,
  queue: Promise.resolve(),
  sections() {
    return [...new Set(['cart-drawer'].concat($$('kp-cart[data-section-id]').map((el) => el.dataset.sectionId)))].join(',');
  },
  run(task) {
    const p = this.queue.then(task, task);
    this.queue = p.catch(() => {});
    return p;
  },
  async fetchJson(url, init) {
    const res = await fetch(url, init);
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.description || json.message || 'Something went wrong. Please try again.');
    return json;
  },
  request(path, body, action) {
    return this.run(async () => {
      cartsBusy(true);
      try {
        const init = { method: 'POST', headers: Object.assign({}, HEADERS), body };
        if (!(body instanceof FormData)) { init.headers['Content-Type'] = 'application/json'; init.body = JSON.stringify(body); }
        const json = await this.fetchJson((((w.Shopify || {}).routes || {}).root || '/') + path, init);
        await this.render(json.sections);
        emit(d, 'kp:cart-updated', { action, response: json, count: this.count });
        return json;
      } catch (err) {
        emit(d, 'kp:cart-error', { action, message: err.message });
        throw err;
      } finally {
        cartsBusy(false);
      }
    });
  },
  add(items, opts) {
    opts = opts || {};
    let body = items;
    if (body instanceof FormData) {
      body.append('sections', this.sections());
      body.append('sections_url', location.pathname);
    } else {
      body = { items: [].concat(items), sections: this.sections(), sections_url: location.pathname };
    }
    return this.request('cart/add.js', body, 'add').then((json) => {
      const dr = drawer();
      if (opts.open !== false && dr && !dr.isOpen) dr.open(opts.opener);
      return json;
    });
  },
  change(id, quantity) {
    quantity = Math.max(0, parseInt(quantity, 10) || 0);
    return this.request('cart/change.js', { id, quantity, sections: this.sections(), sections_url: location.pathname }, quantity ? 'change' : 'remove');
  },
  /* The cart upgrade (snippets/cart-addon.liquid) as ONE queued task with ONE render at the end:
     1. add.js with the bundle, without sections, so the in-between cart (old lines + bundle) is never shown;
     2. /cart.js for fresh line keys and quantities;
     3. update.js taking the old lines out, with sections; render; one kp:cart-updated { action: 'upgrade' }.
     add = { id, quantity }; take = { lineKey: quantity to take out }; opts.changed = the message when a line
     is gone or smaller than expected (the cart changed in another tab).
     If step 2 or 3 fails, the added quantity comes off the bundle's line again (so the cart never keeps both),
     the cart re-renders as it really is, and the error is rethrown (Shopify's own description on a 422).
     Only fetchJson is used in here: request/add/change queue behind this very task and would never start. */
  swap(add, take, opts) {
    opts = opts || {};
    const base = ((w.Shopify || {}).routes || {}).root || '/';
    const post = (path, body) => this.fetchJson(base + path, { method: 'POST', headers: Object.assign({ 'Content-Type': 'application/json' }, HEADERS), body: JSON.stringify(body) });
    const read = () => this.fetchJson(base + 'cart.js', { headers: HEADERS });
    const withSections = (body) => Object.assign(body, { sections: this.sections(), sections_url: location.pathname });
    return this.run(async () => {
      cartsBusy(true);
      let added = false, addedKey = '', done = false;
      try {
        const res = await post('cart/add.js', { items: [{ id: add.id, quantity: add.quantity }] });
        added = true;
        addedKey = ((res.items || [])[0] || {}).key || '';
        const now = await read(), updates = {};
        Object.keys(take).forEach((key) => {
          const line = (now.items || []).find((x) => x.key === key);
          if (!line || line.quantity < take[key]) throw new Error(opts.changed || 'Your cart changed. Please try again.');
          updates[key] = line.quantity - take[key];
        });
        const json = await post('cart/update.js', withSections({ updates }));
        done = true;
        await this.render(json.sections);
        emit(d, 'kp:cart-updated', { action: 'upgrade', response: json, count: this.count });
        return json;
      } catch (err) {
        try {
          if (added && !done) {
            const now = await read();
            const line = (now.items || []).find((x) => (addedKey ? x.key === addedKey : x.variant_id === add.id));
            const json = line ? await post('cart/change.js', withSections({ id: line.key, quantity: Math.max(0, line.quantity - add.quantity) })) : null;
            await this.render(json ? json.sections : null);
          } else if (done) {
            await this.render(null);
          }
        } catch (e) {
          await this.render(null).catch(() => {});
        }
        emit(d, 'kp:cart-error', { action: 'upgrade', message: err.message });
        throw err;
      } finally {
        cartsBusy(false);
      }
    });
  },
  refresh() {
    return this.run(() => this.render(null).then(() => emit(d, 'kp:cart-updated', { action: 'refresh', count: this.count })));
  },
  async render(sections) {
    if (!sections) sections = await this.fetchJson(location.pathname + '?sections=' + this.sections(), { headers: HEADERS });
    let count = NaN;
    Object.keys(sections).forEach((id) => {
      if (!sections[id]) return;
      const doc = new DOMParser().parseFromString(sections[id], 'text/html');
      const c = $('[data-kp-cart-item-count]', doc);
      if (c) count = parseInt(c.dataset.kpCartItemCount, 10);
      $$('kp-cart').forEach((el) => { if (el.dataset.sectionId === id && el.swap) el.swap(doc); });
    });
    if (!isNaN(count)) this.setCount(count);
  },
  setCount(n) {
    this.count = n;
    $$('[data-kp-cart-count]').forEach((el) => { el.textContent = n; el.hidden = !n; el.classList.toggle('is-empty', !n); });
    $$('[data-kp-cart-count-label]').forEach((el) => { el.textContent = (el.dataset.kpCartCountLabel || '[count] items').replace('[count]', n); });
  }
};
/* Screen-reader status line ([data-kp-cart-status]) after every add, change or remove; a beat later so it is read inside the drawer that just opened */
d.addEventListener('kp:cart-updated', (e) => {
  if (e.detail.action === 'refresh') return;
  setTimeout(() => $$('[data-kp-cart-status]').forEach((el) => {
    el.textContent = (el.dataset.kpCartStatusFormat || 'Cart updated. [count] items in your cart.').replace('[count]', e.detail.count);
  }), 60);
});

/* kp-cart */
define('kp-cart', class extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.addEventListener('click', (e) => {
      const t = e.target, line = t.closest('[data-kp-line]');
      const delta = t.closest('[data-kp-qty-plus]') ? 1 : t.closest('[data-kp-qty-minus]') ? -1 : 0, rm = t.closest('[data-kp-remove]');
      if (!line || !(delta || rm)) return;
      e.preventDefault();
      if (rm) { this.change(line, 0); return; }
      const input = $('input[data-kp-qty]', line);
      const qty = Math.max(0, (parseInt(input && input.value, 10) || 0) + delta);
      if (input) input.value = qty;
      this.change(line, qty);
    });
    this.addEventListener('change', (e) => {
      const i = e.target.closest('input[data-kp-qty]');
      if (!i) return;
      const qty = parseInt(i.value, 10);
      this.change(i.closest('[data-kp-line]'), isNaN(qty) ? 1 : Math.max(0, qty));
    });
    /* Enter inside a quantity input stays on the Ajax path (the change above); Checkout still posts the form */
    this.addEventListener('submit', (e) => {
      const a = d.activeElement;
      if (a && a.matches && a.matches('input[data-kp-qty]')) e.preventDefault();
    });
  }
  change(line, qty) {
    if (!line) return;
    this.error('');
    cart.change(line.dataset.kpLine, qty).catch((err) => this.error(err.message));
  }
  error(msg) {
    const el = $('[data-kp-cart-error]', this);
    if (el) { el.textContent = msg; el.hidden = !msg; }
  }
  setBusy(b) {
    this.classList.toggle('is-busy', b);
    if (b) this.setAttribute('aria-busy', 'true'); else this.removeAttribute('aria-busy');
  }
  swap(doc) {
    const fresh = $('kp-cart', doc);
    if (!fresh) return;
    const a = d.activeElement, id = a && this.contains(a) ? a.id : '';
    const regions = $$('[data-kp-region]', fresh);
    if (!regions.length) this.innerHTML = fresh.innerHTML;
    regions.forEach((fr) => {
      const live = $('[data-kp-region="' + fr.dataset.kpRegion + '"]', this);
      if (!live) return;
      live.hidden = fr.hidden;
      if (fr.hasAttribute('data-kp-swap')) live.innerHTML = fr.innerHTML;
    });
    if (!id) return;
    const again = d.getElementById(id);
    const f = again && this.contains(again) ? again : $('[data-kp-close]', this) || $(FOCUS, this);
    if (f) f.focus();
  }
});

/* product form */
const addText = (btn) => $('[data-kp-add-text]', btn) || btn;
/* aria-disabled (not disabled) while adding, so keyboard focus stays on the button if the request fails */
const busy = (btn, on) => {
  if (!btn) return;
  const t = addText(btn);
  if (on) { btn.dataset.kpBusy = '1'; btn.dataset.kpIdle = t.textContent; t.textContent = btn.dataset.textAdding || t.textContent; }
  else { delete btn.dataset.kpBusy; t.textContent = btn.dataset.kpIdle || t.textContent; }
  btn.setAttribute('aria-disabled', String(on));
  btn.setAttribute('aria-busy', String(on));
};
/* opener: the button the shopper actually tapped (e.g. the sticky bar). It shows "Adding…", gets focus back
   when the drawer closes (so the page doesn't jump up to the off-screen buy box), and an error is scrolled into view. */
const submitForm = async (form, opener) => {
  /* The set without a cup size never goes to the cart: the tap scrolls to the sizes instead */
  const host = form.closest('kp-product-form');
  if (host && host.needsChoice && host.promptChoice) { host.promptChoice(); return; }
  const btn = $('[data-kp-add]', form) || $('button[type="submit"]', form);
  if (btn && btn.dataset.kpBusy) return;
  const err = $('[data-kp-form-error]', form.closest('kp-product-form') || form);
  if (err) { err.hidden = true; err.textContent = ''; }
  busy(btn, true);
  if (opener && opener !== btn) busy(opener, true);
  try {
    await cart.add(new FormData(form), { opener: opener || btn });
  } catch (e) {
    if (err) {
      err.textContent = e.message; err.hidden = false;
      if (opener && opener !== btn) err.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
    }
  }
  busy(btn, false);
  if (opener && opener !== btn) busy(opener, false);
};

/* Comma list "a, b" -> ['a', 'b'] (empty items dropped) */
const handles = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);
const cssEsc = (s) => (w.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&'));

/* kp-product-form: the board, kit and set ("Choose your set" cards), one panel + product form each.
   - this.handle is the chosen product; radios input[data-kp-set] (value = handle) choose it.
   - Option groups ([data-kp-option=position][data-kp-option-for=handle]) are found anywhere in the host:
     the set's cup sizes live in its card, not in its panel. Tapping a size also chooses that card.
   - No size picked yet = needsChoice: the buttons read data-text-choose ("Choose a cup size"), a tap
     scrolls to the sizes (never disabled, which would block the tap), the panel's [data-kp-pay]
     (PayPal / Buy now) stays hidden and its variant id input stays disabled. */
define('kp-product-form', class extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.products = {};
    $$('script[data-kp-product]', this).forEach((s) => { try { this.products[s.dataset.kpProduct] = JSON.parse(s.textContent); } catch (e) { /* skip */ } });
    /* The checked card wins (the browser may restore it on Back), then the page's product */
    const checked = $('input[data-kp-set]:checked', this);
    this.handle = (checked && this.products[checked.value] && checked.value) || this.dataset.handle || Object.keys(this.products)[0];
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => {
      const t = e.target;
      if (t.matches('input[data-kp-set]')) { if (!t.checked) return; this.handle = t.value; }
      else if (!t.closest('[data-kp-option]')) return;
      this.update(true);
    });
    this.update(false);
  }
  onClick(e) {
    const b = e.target.closest('[data-kp-option] button[data-kp-value]');
    if (b && this.contains(b)) {
      e.preventDefault();
      const group = b.closest('[data-kp-option]');
      $$('button[data-kp-value]', group).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      group.classList.remove('is-prompted');
      const h = group.dataset.kpOptionFor;
      if (h && this.products[h]) this.handle = h;
      this.update(true);
      return;
    }
    /* The whole card is one tap target: a tap anywhere on it outside its label and controls chooses it too */
    const card = e.target.closest('[data-kp-card]');
    if (!card || !this.contains(card) || e.target.closest('label,button,a,input,select,textarea')) return;
    const r = $('input[data-kp-set]', card);
    if (r && !r.checked) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); }
  }
  get panel() { return this.panelFor(this.handle); }
  panelFor(h) { return $('[data-kp-panel="' + cssEsc(h) + '"]', this) || this; }
  groups(h) { return $$('[data-kp-option-for="' + cssEsc(h) + '"]', this); }
  /* { v: the chosen variant or null, missing: an option group (the cup size) has nothing picked yet } */
  pick(p) {
    const vs = p.variants || [], groups = this.groups(p.handle);
    if (!groups.length) {
      const input = $('input[name="id"]', this.panelFor(p.handle)), id = input && Number(input.value);
      return { v: vs.find((v) => v.id === id) || vs.find((v) => v.available) || vs[0] || null, missing: false };
    }
    const vals = [];
    let missing = false;
    groups.forEach((g) => {
      const b = $('button[data-kp-value][aria-pressed="true"]', g), r = $('input:checked', g), s = $('select', g);
      const val = b ? b.dataset.kpValue : r ? r.value : s && s.value ? s.value : undefined;
      if (val === undefined) missing = true;
      vals[parseInt(g.dataset.kpOption, 10) - 1] = val;
    });
    if (missing) return { v: null, missing: true };
    return { v: vs.find((v) => vals.every((val, i) => val === undefined || (v.options || [])[i] === val)) || null, missing: false };
  }
  /* Kept for other scripts: null while a size is still missing */
  selected(p) { return this.pick(p).v; }
  label(btn, ok, missing, price) {
    if (btn.dataset.kpBusy) return;
    const t = addText(btn);
    if (btn.dataset.textAdd == null) btn.dataset.textAdd = t.textContent.trim();
    btn.disabled = !ok && !missing;
    btn.classList.toggle('is-choose', missing);
    t.textContent = missing ? btn.dataset.textChoose || 'Choose an option'
      : ok ? btn.dataset.textAdd.replace('[price]', price)
      : btn.dataset.textUnavailable || 'Sold out';
  }
  update(byUser) {
    const p = this.products[this.handle];
    if (!p) return;
    const { v, missing } = this.pick(p);
    const ok = !!(v && v.available), price = money(v ? v.price : p.price, this.dataset.moneyFormat);
    const panel = this.panel;
    this.needsChoice = missing;
    $$('[data-kp-panel]', this).forEach((el) => { el.hidden = el.dataset.kpPanel !== this.handle; });
    $$('input[data-kp-set]', this).forEach((r) => {
      r.checked = r.value === this.handle;
      const card = r.closest('[data-kp-card]');
      if (card) card.classList.toggle('is-selected', r.checked);
    });
    if (!missing) $$('.is-prompted', this).forEach((el) => el.classList.remove('is-prompted'));
    [this].concat($$('[data-kp-mirror]')).forEach((r) => {
      $$('[data-kp-title]', r).forEach((el) => { el.textContent = p.title; });
      $$('[data-kp-price]', r).forEach((el) => { el.textContent = price; });
      $$('[data-kp-add]', r).forEach((el) => this.label(el, ok, missing, price));
    });
    /* The variant id goes only into the chosen panel's form. The change event lets Shopify's
       PayPal / Buy now button (same form) follow it. */
    const input = $('input[name="id"][data-kp-variant-id]', panel) || $('input[name="id"]', panel);
    if (input) {
      const before = input.value + '|' + input.disabled;
      if (v) input.value = v.id;
      input.disabled = missing;
      if (input.value + '|' + input.disabled !== before) input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    $$('[data-kp-pay]', panel).forEach((el) => { el.hidden = missing || !v; });
    /* Show only what belongs to the chosen product. An empty or missing list means "every product": never hidden. */
    $$('[data-kp-show-for],[data-kp-included-for]').forEach((el) => {
      const list = handles(el.getAttribute('data-kp-show-for') || el.getAttribute('data-kp-included-for'));
      if (list.length) el.hidden = !list.includes(this.handle);
    });
    /* A tap-to-open row ([data-kp-row]) with no part ([data-kp-row-part]) left for this product hides too */
    $$('[data-kp-row]', this).forEach((row) => { row.hidden = !$$('[data-kp-row-part]', row).some((x) => !x.hidden); });
    /* The address bar follows the chosen product but keeps the query string (utm_*, fbclid...): only ?variant= changes */
    if (byUser && p.url && !(w.Shopify && w.Shopify.designMode)) {
      try {
        const q = new URLSearchParams(location.search);
        if (v && (p.variants || []).length > 1) q.set('variant', v.id); else q.delete('variant');
        const qs = q.toString();
        history.replaceState(history.state, '', new URL(p.url, location.href).pathname + (qs ? '?' + qs : '') + location.hash);
      } catch (e) { /* non-http origin */ }
      if (p.title) { const i = d.title.indexOf(' – '); d.title = p.title + (i > -1 ? d.title.slice(i) : ''); }
    }
    emit(this, 'kp:variant-change', { handle: this.handle, product: p, variant: v, needsChoice: missing });
  }
  /* "Choose a cup size": bring the first size group with nothing picked into view and focus its first button */
  promptChoice() {
    const groups = this.groups(this.handle);
    const g = groups.find((x) => !$('[aria-pressed="true"],input:checked', x)) || groups[0];
    if (!g) return;
    const b = $('button[data-kp-value]', g);
    if (b) b.focus({ preventScroll: true });
    g.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
    g.classList.remove('is-prompted');
    void g.offsetWidth;
    g.classList.add('is-prompted');
  }
  submit(opener) {
    if (this.needsChoice) { this.promptChoice(); return; }
    const f = $('form[data-kp-product-form]', this.panel) || $('form[data-kp-product-form]', this);
    if (f) submitForm(f, opener);
  }
});

/* gallery */
define('kp-gallery', class extends HTMLElement {
  connectedCallback() {
    const track = this.track = $('[data-kp-gallery-track]', this);
    this.slides = track ? $$('[data-kp-slide]', track) : [];
    if (track && !this.slides.length) this.slides = Array.from(track.children);
    this.thumbs = $$('[data-kp-thumb]', this);
    this.counter = $('[data-kp-gallery-counter]', this);
    this.index = Math.max(0, this.thumbs.findIndex((t) => t.getAttribute('aria-pressed') === 'true'));
    this.addEventListener('click', (e) => {
      const t = e.target.closest('[data-kp-thumb]');
      const dir = e.target.closest('[data-kp-prev]') ? -1 : e.target.closest('[data-kp-next]') ? 1 : 0;
      if (!t && !dir) return;
      e.preventDefault();
      this.goTo(t ? this.thumbs.indexOf(t) : this.index + dir);
    });
    this.addEventListener('keydown', (e) => {
      if ((e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') || e.target.closest('input,select,textarea')) return;
      e.preventDefault();
      this.goTo(this.index + (e.key === 'ArrowRight' ? 1 : -1));
      if (e.target.closest('[data-kp-thumb]') && this.thumbs[this.index]) this.thumbs[this.index].focus();
    });
    if (track) {
      let raf = 0;
      track.addEventListener('scroll', () => { raf = raf || requestAnimationFrame(() => { raf = 0; this.sync(); }); }, { passive: true });
    }
    this.goTo(this.index, false);
  }
  sync() {
    const n = this.slides.length;
    if (!n || this.scrolling) return;
    const i = Math.round(this.track.scrollLeft / (this.track.scrollWidth / n));
    if (i !== this.index) this.goTo(i, false);
  }
  goTo(i, scroll) {
    const n = this.slides.length || this.thumbs.length;
    if (!n) return;
    i = ((i % n) + n) % n;
    const changed = i !== this.index;
    this.index = i;
    /* For video slides (assets/kp-video.js) and anything else that cares which slide is showing */
    if (changed) emit(this, 'kp:slide-change', { index: i, slide: this.slides[i] || null });
    this.slides.forEach((s, k) => s.classList.toggle('is-active', k === i));
    this.thumbs.forEach((t, k) => t.setAttribute('aria-pressed', String(k === i)));
    if (this.counter) this.counter.textContent = (this.dataset.format || '{current} / {total}').replace('{current}', i + 1).replace('{total}', n);
    if (scroll !== false && this.slides[i]) {
      const left = this.slides[i].getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      this.scrolling = true;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => { this.scrolling = false; }, 600);
      this.track.scrollTo({ left, behavior: reduced() ? 'auto' : 'smooth' });
    }
  }
});

/* accordion */
define('kp-accordion', class extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const s = e.target.closest('summary'), det = s && s.parentElement;
      if (!det || det.tagName !== 'DETAILS' || e.target.closest('a,button')) return;
      e.preventDefault();
      if (!det.open && this.hasAttribute('single')) $$('details[open]', this).forEach((o) => this.toggle(o, false));
      this.toggle(det, !det.open);
    });
  }
  toggle(det, open) {
    if (reduced() || !det.animate) { det.open = open; return; }
    det.getAnimations().forEach((a) => a.cancel());
    const s = $('summary', det), from = det.offsetHeight;
    if (open) det.open = true;
    const to = open ? det.offsetHeight : s ? s.offsetHeight : 0;
    det.style.overflow = 'hidden';
    const anim = det.animate({ height: [from + 'px', to + 'px'] }, { duration: 260, easing: 'ease-out' });
    anim.onfinish = anim.oncancel = () => {
      det.style.overflow = '';
      if (!open && anim.playState === 'finished') det.open = false;
    };
  }
});

/* delivery */
const addDays = (date, n) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
const addBiz = (date, n) => {
  const step = n < 0 ? -1 : 1;
  for (let i = 0; i < Math.abs(n); i++) {
    date = addDays(date, step);
    while (date.getDay() % 6 === 0) date = addDays(date, step);
  }
  return date;
};
const fmt = (date, year) => date.toLocaleDateString('en-US', year ? { month: 'short', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric' });
define('kp-delivery', class extends HTMLElement {
  connectedCallback() {
    const num = (k, dflt) => { const v = parseInt(this.dataset[k], 10); return isNaN(v) ? dflt : v; };
    const target = $('[data-kp-delivery-text]', this) || this, now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const pMax = num('processingMax', 3), tMax = num('transitMax', 15);
    if (this.dataset.holiday === '1') {
      const by = addBiz(addDays(new Date(today.getFullYear(), 11, 25), -(tMax + 2)), -pMax);
      this.hidden = today >= by;
      if (!this.hidden) target.textContent = (this.dataset.holidayLabel || 'Order by [date] for delivery before December 25').replace('[date]', fmt(by));
      return;
    }
    const start = addDays(addBiz(today, num('processingMin', 1)), num('transitMin', 7));
    const end = addDays(addBiz(today, pMax), tMax);
    const year = start.getFullYear() !== end.getFullYear();
    target.textContent = (this.dataset.format || 'Order today, arrives about {start} – {end}').replace('{start}', fmt(start, year)).replace('{end}', fmt(end, year));
  }
});

/* sticky add to cart (phones, under 900px): shows once the main Add to cart button has scrolled out above
   the screen. data-watch is a selector for the candidate buttons (one per panel); the visible one counts,
   so it follows the chosen set. Hidden while any [data-kp-sticky-hide] element (the final band's button)
   is on screen; product-page.css also hides it while a drawer is open.
   Un-hide first, then add .is-visible a frame later so the slide-in transition can run. */
define('kp-sticky-atc', class extends HTMLElement {
  connectedCallback() {
    if (this.ac || !this.dataset.watch) return;
    this.ac = w.AbortController ? new AbortController() : null;
    const signal = this.ac ? this.ac.signal : undefined;
    this.mq = w.matchMedia('(max-width: 899px)');
    this.hiders = new Set();
    let raf = 0;
    this.queue = () => { raf = raf || requestAnimationFrame(() => { raf = 0; this.check(); }); };
    w.addEventListener('scroll', this.queue, { passive: true, signal });
    w.addEventListener('resize', this.queue, { passive: true, signal });
    d.addEventListener('kp:variant-change', this.queue, { signal });
    d.addEventListener('shopify:section:load', () => this.scan(), { signal });
    if (this.mq.addEventListener) this.mq.addEventListener('change', this.queue, { signal });
    this.scan();
  }
  disconnectedCallback() {
    if (this.ac) this.ac.abort();
    this.ac = null;
    if (this.io) this.io.disconnect();
  }
  /* (Re)collect the buttons to watch and the elements that hide the bar */
  scan() {
    this.targets = $$(this.dataset.watch);
    this.hiders.clear();
    if (this.io) this.io.disconnect();
    if (w.IntersectionObserver) {
      this.io = new IntersectionObserver((en) => {
        en.forEach((x) => { if (x.isIntersecting) this.hiders.add(x.target); else this.hiders.delete(x.target); });
        this.queue();
      });
      $$('[data-kp-sticky-hide]').forEach((el) => this.io.observe(el));
    }
    this.queue();
  }
  check() {
    const t = (this.targets || []).find((el) => el.isConnected && el.getClientRects().length);
    this.show(!!t && this.mq.matches && !this.hiders.size && t.getBoundingClientRect().bottom < 0);
  }
  show(on) {
    if (on === this.on) return;
    this.on = on;
    clearTimeout(this.timer);
    if (on) {
      this.hidden = false;
      void this.offsetHeight;
      requestAnimationFrame(() => { if (!this.hidden) this.classList.add('is-visible'); });
      return;
    }
    this.classList.remove('is-visible');
    if (reduced()) this.hidden = true; else this.timer = setTimeout(() => { this.hidden = true; }, 260);
  }
});

/* reveal */
const io = w.IntersectionObserver && !reduced() ? new IntersectionObserver((entries) => entries.forEach((en) => {
  if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); }
}), { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }) : null;
const reveal = (el) => { if (io) io.observe(el); else el.classList.add('is-visible'); };
define('kp-reveal', class extends HTMLElement {
  connectedCallback() { reveal(this); $$('.reveal', this).forEach(reveal); }
});

/* cart upgrade (snippets/cart-addon.liquid): board -> kit, or kit -> set with a cup size, through cart.swap */
const upgradeBox = (el) => el.closest('[data-kp-upgrade-box]');
/* A cup size: press it, hand its set variant id to the box's button, show that size's amount and price */
const pickUpgradeSize = (b) => {
  const box = upgradeBox(b), group = b.closest('[data-kp-upgrade-sizes]');
  if (!box || !group || box.dataset.kpBusy || b.disabled) return;
  $$('[data-kp-upgrade-size]', group).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
  group.classList.remove('is-prompted');
  if (b.dataset.kpAmount) $$('[data-kp-upgrade-amount]', box).forEach((el) => { el.textContent = b.dataset.kpAmount; });
  if (b.dataset.kpPrice) $$('[data-kp-upgrade-price]', box).forEach((el) => { el.textContent = b.dataset.kpPrice; });
  const btn = $('[data-kp-upgrade]', box);
  if (!btn) return;
  btn.dataset.kpUpgrade = b.dataset.kpUpgradeSize;
  if (btn.dataset.textUpgrade) addText(btn).textContent = btn.dataset.textUpgrade;
};
/* "Choose a cup size" tapped with no size yet: bring the sizes into view, focus the first one, ring them */
const promptUpgradeSize = (box) => {
  const g = box && $('[data-kp-upgrade-sizes]', box);
  if (!g) return;
  const first = $('[data-kp-upgrade-size]:not([disabled])', g);
  if (first) first.focus({ preventScroll: true });
  g.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
  g.classList.add('is-prompted');
};
const upgrade = (t) => {
  const box = upgradeBox(t), host = t.closest('kp-cart');
  if (t.dataset.kpBusy || (box && box.dataset.kpBusy)) return;
  const id = parseInt(t.dataset.kpUpgrade, 10);
  if (!id) { promptUpgradeSize(box); return; }
  let take = {};
  try { take = JSON.parse(t.dataset.kpTake || '{}'); } catch (err) { take = {}; }
  if (!Object.keys(take).length) return;
  const boxId = box ? box.id : '', live = () => (host && host.isConnected ? host : $('kp-cart'));
  /* The whole box is locked while the swap runs, so a second tap (or a size tap) does nothing */
  const hold = (on) => {
    busy(t, on);
    if (!box) return;
    box.classList.toggle('is-busy', on);
    if (on) { box.dataset.kpBusy = '1'; box.setAttribute('aria-busy', 'true'); } else { delete box.dataset.kpBusy; box.removeAttribute('aria-busy'); }
  };
  if (host && host.error) host.error('');
  hold(true);
  cart.swap({ id, quantity: parseInt(t.dataset.kpQuantity, 10) || 1 }, take, { changed: t.dataset.textChanged }).then(() => {
    /* Focus goes back to the upgrade box (the next offer has the same id), else to the new bundle's line */
    const h = live(), next = boxId ? d.getElementById(boxId) : null;
    const f = (next && h && h.contains(next) && next) || (h && ($('[data-kp-line^="' + id + ':"] .cart-item__title', h) || $('[data-kp-close]', h)));
    if (f) f.focus();
  }, (err) => {
    const h = live();
    if (h && h.error) h.error(err.message);
    if (t.isConnected) return;
    const again = boxId ? d.getElementById(boxId) : null;
    if (again) again.focus();
  }).then(() => { if (t.isConnected) hold(false); });
};

/* delegated clicks and submits */
d.addEventListener('click', (e) => {
  const t = e.target.closest('[data-kp-open],[data-kp-upgrade],[data-kp-upgrade-size],[data-kp-submit-product-form]');
  if (!t) return;
  if ('kpOpen' in t.dataset) {
    const dr = drawer(t.dataset.kpOpen);
    if (dr) { e.preventDefault(); dr.open(t); }
    return;
  }
  e.preventDefault();
  if ('kpUpgradeSize' in t.dataset) { pickUpgradeSize(t); return; }
  if ('kpUpgrade' in t.dataset) { upgrade(t); return; }
  const host = $(t.getAttribute('data-kp-submit-product-form') || 'kp-product-form'), f = $('form[data-kp-product-form]');
  if (host && host.submit) host.submit(t); else if (f) submitForm(f, t);
});
d.addEventListener('submit', (e) => {
  if (e.target.matches && e.target.matches('form[data-kp-product-form]')) { e.preventDefault(); submitForm(e.target); }
});

/* Theme editor: selecting a block that sits inside a closed tap-to-open row opens that row */
d.addEventListener('shopify:block:select', (e) => {
  const det = e.target && e.target.closest ? e.target.closest('details') : null;
  if (det) det.open = true;
});

/* init */
const init = () => {
  $$('.reveal').forEach((el) => { if (!el.closest('kp-reveal')) reveal(el); });
  const c = $('[data-kp-cart-item-count]');
  if (c) cart.count = parseInt(c.dataset.kpCartItemCount, 10) || 0;
};
if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init); else init();
w.addEventListener('pageshow', (e) => { if (e.persisted) cart.refresh().catch(() => {}); });

w.KP = { cart, money, drawer };
/* Last: tells the CSS the reveal observer is live (see .js:not(.js-ready) .reveal in base.css) */
root.classList.add('js-ready');
})();
